import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/common/DataTable/DataTable';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useIncidents,
  useIncidentTypes,
  useDeleteIncident,
  useResolveIncident,
  Incident,
  IncidentPriority,
  IncidentStatus,
} from '@/api/hooks/useIncidents';
import {
  Plus,
  Search,
  Filter,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Eye,
  ImageIcon,
  MoreHorizontal,
  Trash2,
  CheckCircle2,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { IncidentDetailDialog } from './components/IncidentDetailDialog';

export default function IncidentsPage() {
  const { t } = useTranslation(['incidents', 'common']);
  const navigate = useNavigate();

  // Status label helper
  const getStatusLabel = (status: IncidentStatus) => {
    switch (status) {
      case 'Open': return t('status.open');
      case 'InProgress': return t('status.inProgress');
      case 'Resolved': return t('status.resolved');
      default: return status;
    }
  };

  // Priority label helper
  const getPriorityLabel = (priority: IncidentPriority) => {
    switch (priority) {
      case 'Critical': return t('priority.critical');
      case 'High': return t('priority.high');
      case 'Medium': return t('priority.medium');
      case 'Low': return t('priority.low');
      default: return priority;
    }
  };

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Dialog state
  const [deleteIncident, setDeleteIncident] = useState<Incident | null>(null);
  const [resolveIncident, setResolveIncident] = useState<Incident | null>(null);
  const [detailIncidentId, setDetailIncidentId] = useState<number | null>(null);

  // Fetch incidents
  const {
    data: incidentsResponse,
    isLoading,
    error,
  } = useIncidents({
    search: searchTerm || undefined,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    typeId: typeFilter ? parseInt(typeFilter) : undefined,
    page,
    pageSize,
  });

  // Fetch incident types
  const { data: incidentTypes } = useIncidentTypes();

  // Mutations
  const deleteMutation = useDeleteIncident();
  const resolveMutation = useResolveIncident();

  // Calculate stats
  const stats = useMemo(() => {
    const items = incidentsResponse?.items ?? [];
    return {
      critical: items.filter((i) => i.priority === 'Critical' && i.status !== 'Resolved').length,
      open: items.filter((i) => i.status === 'Open').length,
      inProgress: items.filter((i) => i.status === 'InProgress').length,
      resolved: items.filter((i) => i.status === 'Resolved').length,
    };
  }, [incidentsResponse]);

  // Handlers
  const handleDelete = async () => {
    if (!deleteIncident) return;
    try {
      await deleteMutation.mutateAsync(deleteIncident.id);
      setDeleteIncident(null);
    } catch (error) {
      console.error('Failed to delete incident:', error);
    }
  };

  const handleResolve = async () => {
    if (!resolveIncident) return;
    try {
      await resolveMutation.mutateAsync({
        id: resolveIncident.id,
        resolution: 'Resolved by user',
      });
      setResolveIncident(null);
    } catch (error) {
      console.error('Failed to resolve incident:', error);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPriorityFilter('');
    setTypeFilter('');
    setPage(1);
  };

  const hasActiveFilters = searchTerm || statusFilter || priorityFilter || typeFilter;

  // Helper functions
  const getPriorityColor = (priority: IncidentPriority) => {
    switch (priority) {
      case 'Critical':
        return 'bg-red-100 text-red-700 hover:bg-red-200';
      case 'High':
        return 'bg-orange-100 text-orange-700 hover:bg-orange-200';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200';
      case 'Low':
        return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
    }
  };

  const getStatusIcon = (status: IncidentStatus) => {
    switch (status) {
      case 'Open':
        return <AlertCircle className="h-4 w-4" />;
      case 'InProgress':
        return <AlertTriangle className="h-4 w-4" />;
      case 'Resolved':
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: IncidentStatus) => {
    switch (status) {
      case 'Open':
        return 'bg-red-100 text-red-700 hover:bg-red-200';
      case 'InProgress':
        return 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200';
      case 'Resolved':
        return 'bg-green-100 text-green-700 hover:bg-green-200';
    }
  };

  // Table columns
  const columns = useMemo<ColumnDef<Incident>[]>(
    () => [
      {
        accessorKey: 'typeName',
        header: t('table.type'),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.typeName}
            {row.original.images.length > 0 && (
              <ImageIcon className="h-3 w-3 text-muted-foreground" />
            )}
          </div>
        ),
      },
      {
        accessorKey: 'assetCode',
        header: t('table.asset'),
        cell: ({ row }) => (
          <Link
            to={`/assets/${row.original.assetId}`}
            className="font-medium text-primary hover:underline"
          >
            {row.original.assetCode}
          </Link>
        ),
      },
      {
        accessorKey: 'description',
        header: t('table.description'),
        cell: ({ row }) => (
          <span className="text-muted-foreground line-clamp-1">
            {row.original.description}
          </span>
        ),
      },
      {
        accessorKey: 'priority',
        header: t('table.priority'),
        cell: ({ row }) => (
          <Badge className={getPriorityColor(row.original.priority)}>
            {getPriorityLabel(row.original.priority)}
          </Badge>
        ),
      },
      {
        accessorKey: 'status',
        header: t('table.status'),
        cell: ({ row }) => (
          <Badge className={getStatusColor(row.original.status)}>
            <span className="flex items-center gap-1">
              {getStatusIcon(row.original.status)}
              {getStatusLabel(row.original.status)}
            </span>
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: t('table.date'),
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {format(new Date(row.original.createdAt), "d 'de' MMM yyyy", { locale: es })}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('table.actions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setDetailIncidentId(row.original.id)}>
                <Eye className="mr-2 h-4 w-4" />
                {t('actions.viewDetails')}
              </DropdownMenuItem>
              {row.original.status !== 'Resolved' && (
                <DropdownMenuItem onClick={() => setResolveIncident(row.original)}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {t('actions.markResolved')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteIncident(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate, t]
  );

  // Pagination handlers
  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pageTitle')}</h1>
          <p className="text-muted-foreground">{t('pageDescription')}</p>
        </div>
        <Button onClick={() => navigate('/incidents/new')}>
          <Plus className="mr-2 h-4 w-4" />
          {t('newIncident')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.critical')}</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.open')}</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.open}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.inProgress')}</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.inProgress}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.resolved')}</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.resolved}</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('cardTitle')}</CardTitle>
          <CardDescription>{t('cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Controls */}
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="pl-8"
                />
              </div>
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {t('common:buttons.filters')}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2">
                    {t('common:status.active')}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="flex flex-wrap items-center gap-4 p-4 rounded-lg border bg-muted/50">
                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('filters.status')}</label>
                  <Select
                    value={statusFilter || 'all'}
                    onValueChange={(v) => {
                      setStatusFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('status.all')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('status.all')}</SelectItem>
                      <SelectItem value="Open">{t('status.open')}</SelectItem>
                      <SelectItem value="InProgress">{t('status.inProgress')}</SelectItem>
                      <SelectItem value="Resolved">{t('status.resolved')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('filters.priority')}</label>
                  <Select
                    value={priorityFilter || 'all'}
                    onValueChange={(v) => {
                      setPriorityFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('priority.all')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('priority.all')}</SelectItem>
                      <SelectItem value="Critical">{t('priority.critical')}</SelectItem>
                      <SelectItem value="High">{t('priority.high')}</SelectItem>
                      <SelectItem value="Medium">{t('priority.medium')}</SelectItem>
                      <SelectItem value="Low">{t('priority.low')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex-1 min-w-[150px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('filters.type')}</label>
                  <Select
                    value={typeFilter || 'all'}
                    onValueChange={(v) => {
                      setTypeFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('filters.allTypes')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('filters.allTypes')}</SelectItem>
                      {incidentTypes?.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    {t('filters.clearFilters')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Data Table */}
          {error ? (
            <div className="text-center py-8 text-destructive">
              {t('messages.errorLoading')}
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={incidentsResponse?.items ?? []}
              pageCount={incidentsResponse?.totalPages ?? 1}
              pageIndex={page - 1}
              pageSize={pageSize}
              onPageChange={(pageIndex) => handlePageChange(pageIndex + 1)}
              onPageSizeChange={handlePageSizeChange}
              totalItems={incidentsResponse?.totalCount ?? 0}
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteIncident}
        onOpenChange={(open) => !open && setDeleteIncident(null)}
        title={t('dialog.deleteTitle')}
        description={t('dialog.deleteConfirm', { id: deleteIncident?.id })}
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Resolve Confirmation Dialog */}
      <ConfirmDialog
        open={!!resolveIncident}
        onOpenChange={(open) => !open && setResolveIncident(null)}
        title={t('dialog.resolveTitle')}
        description={t('dialog.resolveConfirm', { id: resolveIncident?.id })}
        confirmLabel={t('actions.markResolved')}
        cancelLabel={t('common:buttons.cancel')}
        onConfirm={handleResolve}
        isLoading={resolveMutation.isPending}
      />

      {/* Incident Detail Dialog */}
      {detailIncidentId !== null && (
        <IncidentDetailDialog
          open={detailIncidentId !== null}
          onOpenChange={(open) => !open && setDetailIncidentId(null)}
          mode="single"
          incidentId={detailIncidentId}
        />
      )}
    </div>
  );
}
